import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { AlertTriangle, ArrowRight, CheckCircle, X, Merge } from 'lucide-react';
import { conflictResolver, Conflict } from '../utils/offline/conflictResolver';
import { toast } from 'sonner';

interface ConflictResolverProps {
  onResolved?: () => void;
  onClose?: () => void;
}

export default function ConflictResolver({ onResolved, onClose }: ConflictResolverProps) {
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [selectedConflict, setSelectedConflict] = useState<Conflict | null>(null);
  const [resolutions, setResolutions] = useState<Record<string, 'local' | 'server' | 'merge'>>({});

  useEffect(() => {
    loadConflicts();
  }, []);

  const loadConflicts = () => {
    const allConflicts = conflictResolver.getConflicts();
    setConflicts(allConflicts);
  };

  const handleResolveConflict = (conflictId: string, resolution: 'local' | 'server' | 'merge') => {
    setResolutions(prev => ({ ...prev, [conflictId]: resolution }));
  };

  const handleApplyResolutions = () => {
    let resolvedCount = 0;

    for (const [conflictId, resolution] of Object.entries(resolutions)) {
      try {
        conflictResolver.resolveConflict(conflictId, resolution);
        resolvedCount++;
      } catch (error) {
        console.error('Failed to resolve conflict:', error);
      }
    }

    if (resolvedCount > 0) {
      toast.success(`Resolved ${resolvedCount} ${resolvedCount === 1 ? 'conflict' : 'conflicts'}`);
      setResolutions({});
      loadConflicts();
      onResolved?.();
    }

    if (conflicts.length === resolvedCount) {
      onClose?.();
    }
  };

  const handleResolveAll = (resolution: 'local' | 'server') => {
    const newResolutions: Record<string, 'local' | 'server' | 'merge'> = {};
    conflicts.forEach(conflict => {
      newResolutions[conflict.id] = resolution;
    });
    setResolutions(newResolutions);
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'None';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const formatFieldName = (field: string): string => {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const getEntityColor = (entity: string) => {
    switch (entity) {
      case 'well': return 'bg-blue-100 text-blue-800';
      case 'entry': return 'bg-green-100 text-green-800';
      case 'message': return 'bg-purple-100 text-purple-800';
      case 'task': return 'bg-orange-100 text-orange-800';
      case 'note': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (conflicts.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No Conflicts Found
          </h3>
          <p className="text-gray-600 mb-6">
            All your changes are in sync!
          </p>
          <Button onClick={onClose} className="w-full h-12">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold flex items-center space-x-2">
            <AlertTriangle className="h-6 w-6" />
            <span>Resolve Conflicts</span>
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/20 active:bg-white/30 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <p className="text-sm text-white/90">
          {conflicts.length} {conflicts.length === 1 ? 'conflict' : 'conflicts'} detected - Choose which version to keep
        </p>
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-50 border-b p-4">
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => handleResolveAll('local')}
            variant="outline"
            className="h-11"
          >
            Keep All Mine
          </Button>
          <Button
            onClick={() => handleResolveAll('server')}
            variant="outline"
            className="h-11"
          >
            Keep All Server
          </Button>
        </div>
      </div>

      {/* Conflicts List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {conflicts.map((conflict) => {
          const resolution = resolutions[conflict.id];
          
          return (
            <Card key={conflict.id} className="border-2 border-orange-200">
              <CardContent className="p-4">
                {/* Conflict Header */}
                <div className="flex items-center justify-between mb-3">
                  <Badge className={getEntityColor(conflict.entity)}>
                    {conflict.entity}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {formatFieldName(conflict.field)}
                  </Badge>
                </div>

                {/* Value Comparison */}
                <div className="space-y-3 mb-4">
                  {/* Local Value */}
                  <button
                    onClick={() => handleResolveConflict(conflict.id, 'local')}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      resolution === 'local'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                        <span className="text-sm font-semibold text-gray-900">Your Version</span>
                      </div>
                      {resolution === 'local' && (
                        <CheckCircle className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-gray-700 font-mono bg-gray-50 p-2 rounded">
                      {formatValue(conflict.localValue)}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Modified {new Date(conflict.localTimestamp).toLocaleString()}
                    </p>
                  </button>

                  {/* Server Value */}
                  <button
                    onClick={() => handleResolveConflict(conflict.id, 'server')}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      resolution === 'server'
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-green-600"></div>
                        <span className="text-sm font-semibold text-gray-900">Server Version</span>
                      </div>
                      {resolution === 'server' && (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                    <p className="text-sm text-gray-700 font-mono bg-gray-50 p-2 rounded">
                      {formatValue(conflict.serverValue)}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Modified {new Date(conflict.serverTimestamp).toLocaleString()}
                    </p>
                  </button>

                  {/* Merge Option (for compatible types) */}
                  {(typeof conflict.localValue === 'object' || typeof conflict.localValue === 'string') && (
                    <button
                      onClick={() => handleResolveConflict(conflict.id, 'merge')}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        resolution === 'merge'
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-2">
                          <Merge className="h-4 w-4 text-purple-600" />
                          <span className="text-sm font-semibold text-gray-900">Merge Both</span>
                        </div>
                        {resolution === 'merge' && (
                          <CheckCircle className="h-5 w-5 text-purple-600" />
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        Automatically combine both versions
                      </p>
                    </button>
                  )}
                </div>

                {/* Resolution Status */}
                {resolution && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 flex items-center justify-between">
                    <span className="text-sm text-green-800 font-medium">
                      Resolution selected: {resolution}
                    </span>
                    <button
                      onClick={() => {
                        const newResolutions = { ...resolutions };
                        delete newResolutions[conflict.id];
                        setResolutions(newResolutions);
                      }}
                      className="text-green-600 hover:text-green-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Footer Actions */}
      <div className="border-t p-4 bg-white space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            {Object.keys(resolutions).length} of {conflicts.length} resolved
          </span>
          {Object.keys(resolutions).length < conflicts.length && (
            <span className="text-orange-600 font-medium">
              {conflicts.length - Object.keys(resolutions).length} remaining
            </span>
          )}
        </div>
        
        <Button
          onClick={handleApplyResolutions}
          disabled={Object.keys(resolutions).length === 0}
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300"
        >
          <CheckCircle className="h-5 w-5 mr-2" />
          Apply Resolutions ({Object.keys(resolutions).length})
        </Button>
      </div>
    </div>
  );
}
